import type { CartItem } from '@/modelos/comprobantes/comprobante.types';
import type { Product as ProductoCatalogo } from '@/features/catalogo-articulos/models/types';
import { describeUnitConversion } from '@/shared/inventory/unitConversion';

// ─── Interfaces públicas ──────────────────────────────────────────────────────

export interface LinePricingInput {
  unidadMinimaCode: string;
  unidadSeleccionadaCode: string;
  /** Cuántas unidades mínimas contiene 1 unidad seleccionada (ej: 1 CAJA = 10 unidades). */
  factorToUnidadMinima: number;
  cantidad: number;
  /** Precio por 1 unidad mínima (sin importar cómo se vende). */
  precioBaseUnidadMinima: number;
  /** Precio específico para esta presentación. Si viene null/undefined se calcula desde la unidad mínima. */
  precioPresentacionOpcional?: number | null;
  /** Tasa decimal de IGV (ej: 0.18 para 18%). */
  igvRate: number;
  /** true = el precio ya incluye IGV; false = precio neto. */
  precioIncluyeIgv: boolean;
  /** Descuento fijo en moneda del documento. El resultado nunca baja de 0. */
  descuentoMonto?: number;
  /** Decimales para redondeo (por defecto 2). */
  currencyPrecision?: number;
}

export interface LinePricingResult {
  precioUnitario: number;
  /** cantidad × precioUnitario, con descuento aplicado. */
  importe: number;
  subtotal: number;
  igv: number;
  total: number;
  /** Cantidad convertida a unidad mínima (para descontar stock). */
  cantidadEnUnidadMinima: number;
  usoPrecioPresentacion: boolean;
}

// ─── Helpers internos ─────────────────────────────────────────────────────────

const TIPOS_SIN_IGV = new Set(['exonerado', 'inafecto', 'exportacion']);

function redondear(valor: number, decimales: number): number {
  if (!Number.isFinite(valor)) return 0;
  const factor = Math.pow(10, decimales);
  return Math.round((valor + Number.EPSILON) * factor) / factor;
}

function resolverPrecioUnitario(
  input: LinePricingInput,
  precision: number,
): { precioUnitario: number; usoPrecioPresentacion: boolean } {
  const tienePrecioPresentacion = input.precioPresentacionOpcional != null;

  if (tienePrecioPresentacion) {
    return {
      precioUnitario: redondear(input.precioPresentacionOpcional as number, precision),
      usoPrecioPresentacion: true,
    };
  }

  return {
    precioUnitario: redondear(input.precioBaseUnidadMinima * input.factorToUnidadMinima, precision),
    usoPrecioPresentacion: false,
  };
}

export const tasaIgvDesdeItem = (item: CartItem): number => {
  if (item.igvType && TIPOS_SIN_IGV.has(item.igvType)) return 0;
  if (typeof item.igv === 'number' && item.igv > 0) return item.igv / 100;
  return 0;
};

// ─── Cálculo de línea ─────────────────────────────────────────────────────────

export function calculateLineaComprobante(input: LinePricingInput): LinePricingResult {
  const precision = input.currencyPrecision ?? 2;
  const cantidad = Number.isFinite(input.cantidad) && input.cantidad > 0 ? input.cantidad : 0;
  const factor = Number.isFinite(input.factorToUnidadMinima) && input.factorToUnidadMinima > 0
    ? input.factorToUnidadMinima
    : 0;

  const { precioUnitario, usoPrecioPresentacion } = resolverPrecioUnitario(input, precision);
  const importe = redondear(cantidad * precioUnitario, precision);

  const descuento = Number.isFinite(input.descuentoMonto) && (input.descuentoMonto as number) > 0
    ? redondear(input.descuentoMonto as number, precision)
    : 0;
  const importeConDescuento = Math.max(0, importe - descuento);

  let subtotal: number;
  let igv: number;
  let total: number;

  if (input.precioIncluyeIgv) {
    const divisor = 1 + input.igvRate;
    total = importeConDescuento;
    subtotal = redondear(total / divisor, precision);
    igv = redondear(total - subtotal, precision);
  } else {
    subtotal = importeConDescuento;
    igv = redondear(subtotal * input.igvRate, precision);
    total = redondear(subtotal + igv, precision);
  }

  return {
    precioUnitario,
    importe: importeConDescuento,
    subtotal,
    igv,
    total,
    cantidadEnUnidadMinima: redondear(cantidad * factor, 6),
    usoPrecioPresentacion,
  };
}

// ─── Constructor de input desde CartItem ─────────────────────────────────────

export const buildLinePricingInputFromCartItem = (
  item: CartItem,
  productoCatalogo?: ProductoCatalogo,
  opciones?: { priceIncludesTax?: boolean },
): LinePricingInput => {
  const codigoUnidad = item.unidadMedida || item.unit || '';
  const conversion = describeUnitConversion(productoCatalogo, codigoUnidad);
  const factor = conversion.factorToUnidadMinima > 0 ? conversion.factorToUnidadMinima : 1;
  const precio = Number.isFinite(item.price) ? (item.price as number)
    : Number.isFinite(item.basePrice) ? (item.basePrice as number)
    : 0;

  return {
    unidadMinimaCode: conversion.unidadMinima,
    unidadSeleccionadaCode: conversion.unidadSeleccionada,
    factorToUnidadMinima: factor,
    cantidad: Number.isFinite(item.quantity) ? (item.quantity as number) : 0,
    precioBaseUnidadMinima: precio / factor,
    precioPresentacionOpcional: precio,
    igvRate: tasaIgvDesdeItem(item),
    precioIncluyeIgv: opciones?.priceIncludesTax ?? true,
    descuentoMonto: Number.isFinite(item.descuentoItem) && (item.descuentoItem as number) > 0
      ? (item.descuentoItem as number)
      : undefined,
    currencyPrecision: 2,
  };
};

export interface PreciosBrutoLinea {
  valorVentaUnitario: number;
  precioVentaUnitario: number;
  valorVentaTotal: number;
  precioVentaTotal: number;
}

interface OpcionesPrecioParaCarrito {
  preciosIncluyenIgv: boolean;
  monedaOrigen?: string;
  convertirAMonedaBase?: (monto: number, monedaOrigen: string) => number;
}

export function precioUnitarioParaCarrito(
  item: { precioVentaUnitario?: number | null; valorVentaUnitario?: number | null; precioUnitario?: number | null },
  { preciosIncluyenIgv, monedaOrigen, convertirAMonedaBase }: OpcionesPrecioParaCarrito,
): number {
  const preferido = preciosIncluyenIgv ? item.precioVentaUnitario : item.valorVentaUnitario;
  const alterno = preciosIncluyenIgv ? item.valorVentaUnitario : item.precioVentaUnitario;
  const precio = preferido ?? alterno ?? item.precioUnitario ?? 0;
  return monedaOrigen && convertirAMonedaBase ? convertirAMonedaBase(precio, monedaOrigen) : precio;
}

export function calcularPreciosBrutoLinea(
  item: CartItem,
  pricesIncludeTax: boolean,
  exchangeRate = 1,
): PreciosBrutoLinea {
  const tasaIgv = tasaIgvDesdeItem(item);
  const divisorIgv = 1 + tasaIgv;
  const cantidad = Number.isFinite(item.quantity) ? (item.quantity as number) : 0;
  const rate = exchangeRate > 0 ? exchangeRate : 1;
  const precioEntrada = (Number.isFinite(item.price) ? (item.price as number) : 0) / rate;

  const valorVentaUnitario = pricesIncludeTax ? precioEntrada / divisorIgv : precioEntrada;
  const precioVentaUnitario = pricesIncludeTax ? precioEntrada : precioEntrada * divisorIgv;

  return {
    valorVentaUnitario,
    precioVentaUnitario,
    valorVentaTotal: valorVentaUnitario * cantidad,
    precioVentaTotal: precioVentaUnitario * cantidad,
  };
}

export const descuentoMontoLinea = (item: CartItem): number => item.descuentoItem ?? 0;

export const algunItemTieneDescuento = (cartItems: CartItem[]): boolean =>
  cartItems.some(item => descuentoMontoLinea(item) > 0);

// ─── Cálculo inverso (edición bidireccional por ítem) ────────────────────────

export interface ResolverCantidadDesdeSubtotalParams {
  subtotal: number;
  precioUnitario: number;
  descuentoProductoPct?: number;
  igvRate: number;
  precioIncluyeIgv: boolean;
}

export function resolverCantidadDesdeSubtotal(params: ResolverCantidadDesdeSubtotalParams): number | null {
  const { subtotal, precioUnitario, igvRate, precioIncluyeIgv } = params;
  const pct = Number.isFinite(params.descuentoProductoPct) ? (params.descuentoProductoPct as number) : 0;
  const factorDescuento = 1 - pct / 100;
  const subtotalFactor = precioIncluyeIgv ? 1 + igvRate : 1;

  if (!Number.isFinite(subtotal) || subtotal < 0) return null;
  if (!Number.isFinite(precioUnitario) || precioUnitario <= 0) return null;
  if (!Number.isFinite(factorDescuento) || factorDescuento <= 0) return null;

  const cantidad = (subtotal * subtotalFactor) / (precioUnitario * factorDescuento);
  return Number.isFinite(cantidad) && cantidad >= 0 ? cantidad : null;
}

export interface ResolverPrecioDesdeTotalParams {
  total: number;
  cantidad: number;
  descuentoProductoPct?: number;
  igvRate: number;
  precioIncluyeIgv: boolean;
}

export function resolverPrecioDesdeTotal(params: ResolverPrecioDesdeTotalParams): number | null {
  const { total, cantidad, igvRate, precioIncluyeIgv } = params;
  const pct = Number.isFinite(params.descuentoProductoPct) ? (params.descuentoProductoPct as number) : 0;
  const factorDescuento = 1 - pct / 100;
  const totalFactor = precioIncluyeIgv ? 1 : 1 + igvRate;

  if (!Number.isFinite(total) || total < 0) return null;
  if (!Number.isFinite(cantidad) || cantidad <= 0) return null;
  if (!Number.isFinite(factorDescuento) || factorDescuento <= 0) return null;
  if (!Number.isFinite(totalFactor) || totalFactor <= 0) return null;

  const precio = total / (cantidad * factorDescuento * totalFactor);
  return Number.isFinite(precio) && precio >= 0 ? precio : null;
}
